import React from "react";
import { motion } from "framer-motion";
import { BrainIcon, StethoscopeIcon, ClipboardListIcon, HeartHandshakeIcon, GraduationCapIcon, CalendarCheckIcon, TerminalSquareIcon, LineChartIcon, BoxIcon } from "lucide-react";
import { SectionTag } from "./SectionTag";
import { KITS, Kit } from "./salesData";
const KIT_ICONS: Record<string, BoxIcon> = {
  asistente: BrainIcon,
  consulta: StethoscopeIcon,
  planes: ClipboardListIcon,
  adherencia: HeartHandshakeIcon,
  educacion: GraduationCapIcon,
  gestion: CalendarCheckIcon,
  comandos: TerminalSquareIcon,
  datos: LineChartIcon
};

/** Kits 01 y 07 rompen la monotonía de la grilla con tratamiento destacado. */
const HIGHLIGHT_IDS = ['asistente', 'comandos'];
function KitCard({
  kit
}: {
  kit: Kit;
}) {
  const Icon = KIT_ICONS[kit.id];
  const highlight = HIGHLIGHT_IDS.includes(kit.id);
  return <article className={`flex h-full flex-col rounded-3xl border p-5 transition-transform duration-200 ease-out hover:-translate-y-1 sm:p-7 ${highlight ? 'border-charcoal bg-charcoal shadow-[0_24px_50px_-30px_rgba(34,34,34,0.6)]' : 'border-warm bg-ivory shadow-[0_18px_40px_-32px_rgba(34,34,34,0.5)]'} ${highlight ? 'lg:flex-row lg:items-center lg:gap-10' : ''}`} data-id="element-145" data-mp="2xkgbastrkbwt">
      <div className={highlight ? 'lg:w-1/2' : ''} data-id="element-146" data-mp="p4ajfp02rbv2">
        <div className="flex items-center justify-between gap-4" data-id="element-147" data-mp="u458ro47bte0">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${highlight ? 'bg-gold' : 'bg-gold-pale'}`} aria-hidden data-id="element-148" data-mp="u2c3ac5brf7l">
            <Icon className={`h-5 w-5 ${highlight ? 'text-charcoal' : 'text-gold-deep'}`} data-id="element-149" data-mp="l4i7d2bh2nky" />
          </span>
          <span className={`font-display text-[2.1rem] leading-none sm:text-[2.6rem] ${highlight ? 'text-ivory/15' : 'text-charcoal/8'}`} aria-hidden data-id="element-150" data-mp="67hqjtutkd19">
            {kit.number}
          </span>
        </div>

        <p className={`mt-4 font-sans text-[0.58rem] font-bold uppercase tracking-editorial sm:text-[0.64rem] ${highlight ? 'text-gold-soft' : 'text-gold-deep'}`} data-id="element-151" data-mp="20lil42jc6dvj">
          Kit {kit.number} · {kit.category}
        </p>
        <h3 className={`mt-2 font-display text-[1.1rem] uppercase leading-tight sm:text-[1.35rem] ${highlight ? 'text-ivory' : 'text-charcoal'}`} data-id="element-152" data-mp="243g25ihl3d8p">
          {kit.name}
        </h3>
        <p className={`mt-3 font-sans text-[0.85rem] leading-relaxed sm:text-[0.94rem] ${highlight ? 'text-ivory/70' : 'text-charcoal/65'}`} data-id="element-153" data-mp="1u4h5kya3f822">
          {kit.benefit}
        </p>
      </div>

      <ul className={`mt-5 flex flex-wrap gap-2 ${highlight ? 'lg:mt-0 lg:w-1/2 lg:content-center' : ''}`} data-id="element-154" data-mp="2mrbcesdzm8x2">
        {kit.capabilities.map(capability => <li key={capability} className={`rounded-full border px-3 py-1.5 font-sans text-[0.72rem] font-semibold sm:text-[0.78rem] ${highlight ? 'border-ivory/20 bg-charcoal-soft text-ivory/85' : 'border-warm bg-bone text-charcoal/75'}`} data-id="element-155" data-mp="2hvw38dqxd16z">
            {capability}
          </li>)}
      </ul>
    </article>;
}
export function KitsSection() {
  return <section id="kits" className="bg-ivory py-14 sm:py-24" aria-labelledby="kits-title" data-id="element-156" data-mp="30rcnt1rvoyqs">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-8" data-id="element-157" data-mp="najd15z7r4n2">
        <div className="max-w-2xl" data-id="element-158" data-mp="nc47ywpfwplg">
          <SectionTag data-id="element-159" data-mp="s82ro6ztpjfj">Qué incluye</SectionTag>
          <h2 id="kits-title" className="mt-4 font-display text-[1.6rem] uppercase leading-[1.06] text-charcoal sm:text-[2.5rem]" data-id="element-160" data-mp="sayott3uk42x">
            Los 8 kits, uno por uno
          </h2>
          <p className="mt-4 font-sans text-[0.9rem] leading-relaxed text-charcoal/65 sm:text-base" data-id="element-161" data-mp="11b3u9xphcseg">
            Cada kit resuelve un área concreta del trabajo diario. Se usan por separado o como
            sistema completo.
          </p>
        </div>

        <div className="mt-9 grid gap-4 sm:mt-12 sm:gap-5 lg:grid-cols-2" data-id="element-162" data-mp="1fxx1efouirvk">
          {KITS.map((kit, index) => <motion.div key={kit.id} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.25
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1],
          delay: Math.min(index % 2, 1) * 0.05
        }} className={HIGHLIGHT_IDS.includes(kit.id) ? 'lg:col-span-2' : ''} data-id="element-163" data-mp="1k0jyav0dj2zm" data-mpi={index}>
              <KitCard kit={kit} data-id="element-164" data-mp="1bbmpwu7xlvqg" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}